import { useState } from 'react'
import { NavLink, useNavigate } from 'react-router-dom'
import { useSession } from '../../session'
import { completeOnboardingStep, getOnboardingContext, nextAfter } from '../../utils/onboarding'

// Pieces shared by the Master Files pages: the section tabs, labelled form
// fields, and the "saved — what next?" step that new companies see while
// setting up.

/** Who may open each Master Files page (the same rules as the routes). */
export function useMasterAccess() {
  const { roles = [], has: planHas = () => true } = useSession()
  const has = (...r) => r.some((x) => roles.includes(x))
  const admin = has('admin')
  return {
    admin,
    items: has('admin', 'sales', 'purchase', 'inventory'),
    customers: has('admin', 'sales'),
    vendors: has('admin', 'purchase', 'inventory'),
    locations: admin,
    users: admin,
    accounts: admin && planHas('accounting'), // the books are Accounting's
    stock: has('admin', 'inventory') && planHas('inventory'), // stock pages and pick bins
    tracked: planHas('inventory'), // stock is counted (the plan has Inventory)
  }
}

export function MasterTabs() {
  const can = useMasterAccess()
  const tabs = [
    { to: '/master', label: 'Summary', end: true, show: true },
    { to: '/master/products', label: 'Products & services', show: can.items },
    { to: '/master/customers', label: 'Customers', show: can.customers },
    { to: '/master/vendors', label: 'Vendors', show: can.vendors },
    { to: '/master/locations', label: 'Locations', show: can.locations },
    { to: '/master/users', label: 'Users', show: can.users },
    { to: '/master/accounts', label: 'Chart of accounts', show: can.accounts },
  ].filter((t) => t.show)
  return (
    <nav className="section-tabs" aria-label="Master files">
      {tabs.map(({ to, label, end }) => <NavLink key={to} to={to} end={end}>{label}</NavLink>)}
    </nav>
  )
}

/** A labelled input with an optional hint and error. */
export function Field({ label, hint, error, className = '', children }) {
  return (
    <label className={`ci-form-field${className ? ` ${className}` : ''}`}>
      <span>{label}</span>
      {children}
      {hint && !error && <span className="mf-hint">{hint}</span>}
      {error && <span className="acc-field-error">{error}</span>}
    </label>
  )
}

/** "Saved — add another or continue?" while a new company is setting up.
    afterSave() returns true when the prompt took over (stay on the page). */
/** A step's page during Getting started: afterSave() opens the "add more
    or go on" prompt (and says whether it did), `after` is what the prompt
    offers next, next() marks the step done and moves on. */
export function useOnboardingStep(step) {
  const nav = useNavigate()
  const [open, setOpen] = useState(false)
  const [after, setAfter] = useState(null)
  return {
    open,
    after,
    afterSave() {
      const ctx = getOnboardingContext()
      if (ctx?.inOnboarding && ctx?.activeStep === step) {
        setAfter(nextAfter(step))
        setOpen(true)
        return true
      }
      return false
    },
    addAnother() { setOpen(false) },
    next() {
      setOpen(false)
      const done = completeOnboardingStep(step)
      if (done?.nextRoute) setTimeout(() => nav(done.nextRoute), 300)
    },
  }
}

export const isService = (item) => Number(item?.is_service || 0) === 1
export const isTaxable = (item) => Number(item?.is_taxable ?? 1) === 1

/** "10%" or "$2.00 off", or '' when the item has no discount. */
export const discountText = (item, money) => {
  const type = String(item?.discount_type || '').toLowerCase()
  const value = Number(item?.discount_value || 0)
  if (!type || type === 'none' || value <= 0) return ''
  return type === 'percent' ? `${value}% off` : `${money(value)} off`
}

export const customerName = (c) => `${c?.first_name || ''} ${c?.last_name || ''}`.trim() || `Customer #${c?.customer_id}`

/** Street, city, state, postal code and country fields for a form object. */
export function AddressFields({ form, set }) {
  return (
    <>
      <div className="ci-form-meta-top one">
        <Field label="Address">
          <input value={form.address} onChange={(e) => set({ address: e.target.value })} placeholder="Street and number" />
        </Field>
      </div>
      <div className="ci-form-meta-top four">
        <Field label="City"><input value={form.city} onChange={(e) => set({ city: e.target.value })} /></Field>
        <Field label="State"><input value={form.state} onChange={(e) => set({ state: e.target.value })} /></Field>
        <Field label="Postal code"><input value={form.postal_code} onChange={(e) => set({ postal_code: e.target.value })} /></Field>
        <Field label="Country"><input value={form.country} onChange={(e) => set({ country: e.target.value })} /></Field>
      </div>
    </>
  )
}

export const validEmail = (v) => !String(v || '').trim() || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(v).trim())
export const placeLine = (r) => [r?.city, r?.state].filter(Boolean).join(', ')

/** The roles a user can have (stored values), with what each one opens. */
export const ROLES = [
  { value: 'admin', label: 'Admin', desc: 'Everything, including users, locations, setup and the books.' },
  { value: 'accountant', label: 'Accountant', desc: 'Accounting, reports and expenses.' },
  { value: 'cashier', label: 'Cashier', desc: 'Only the point of sale (POS): sales and refunds. Free: cashiers don’t count toward your plan’s users.' },
  { value: 'sales', label: 'Sales', desc: 'Point of sale (POS), quotes, invoices and customers.' },
  { value: 'purchase', label: 'Purchase', desc: 'Purchase orders, goods received, vendor bills and vendors.' },
  { value: 'inventory', label: 'Inventory', desc: 'Stock, counts, moves, and products and services.' },
  { value: 'operating_cost', label: 'Expenses', desc: 'Recording the costs of running the business.' },
]
export const ROLE_LABEL = Object.fromEntries(ROLES.map((r) => [r.value, r.label]))
export const roleList = (role) => String(role || '').split(',').map((s) => s.trim()).filter(Boolean)
export const userActive = (u) => String(u?.is_active ?? '1') === '1'
/** A user whose only role is cashier: the POS only, free on every plan. */
export const cashierOnly = (role) => { const r = roleList(role).map((x) => x.toLowerCase()); return r.length > 0 && r.every((x) => x === 'cashier') }
export const photoSrc = (path) => (!path ? '' : (String(path).startsWith('http') ? path : `${import.meta.env.VITE_API_URL || ''}${path}`))

export const ACCOUNT_TYPES = [['Asset', 'Assets'], ['Liability', 'Liabilities'], ['Equity', 'Equity'], ['Revenue', 'Revenue'], ['Expense', 'Expenses']]

/** A heading account: one that nothing is posted to directly (code ending 00, or with accounts under it). */
export const isHeading = (a, all = []) => !a?.parent_account_id && (String(a?.account_code || '').endsWith('00') || all.some((x) => x.parent_account_id === a?.account_id))

/** Every account a transaction can post to, by the name it's saved under.
    The first 22 are shared defaults every company has; the rest are a
    transaction's own accounts, which use the shared one they stand for
    (inherits) until a different account is chosen. */
export const ACCOUNT_ROLES = {
  Cash: { label: 'Cash', type: 'Asset', help: 'Cash in the drawer or safe.' },
  Card: { label: 'Bank and card', type: 'Asset', help: 'Your bank account: where card payments and transfers land.' },
  AR: { label: 'Accounts receivable', type: 'Asset', help: 'What customers owe you. Invoices, payments, credit memos and sales on account all use it, so the amounts owed stay in step.' },
  AP: { label: 'Accounts payable', type: 'Liability', help: 'What you owe vendors. Bills, payments and expenses on account all use it, so the amounts owed stay in step.' },
  Sales: { label: 'Product sales', type: 'Revenue', help: 'Revenue from products sold.' },
  ServiceRevenue: { label: 'Service revenue', type: 'Revenue', help: 'Revenue from services sold.' },
  SalesDiscounts: { label: 'Sales discounts and returns', type: 'Revenue', help: 'Discounts given to customers and refunds. They reduce sales on the income statement.' },
  SalesTaxPayable: { label: 'Sales tax owed', type: 'Liability', help: 'Sales tax collected from customers that you owe the tax office.' },
  DeferredRevenue: { label: 'Customer deposits', type: 'Liability', help: 'Money received before you’ve delivered, such as deposits on jobs. It’s a contract liability, shown with current liabilities.' },
  ContractAssets: { label: 'Jobs: earned, not yet billed', type: 'Asset', help: 'For long jobs: work done that hasn’t been invoiced yet (a contract asset). Month-end close books it, and reverses it on the 1st.' },
  ContractLiabilities: { label: 'Jobs: billed, not yet earned', type: 'Liability', help: 'For long jobs: invoiced ahead of the work (a contract liability), and any expected loss not covered by work done. Month-end close books it, and reverses it on the 1st.' },
  Retainage: { label: 'Jobs: retainage held by customers', type: 'Asset', help: 'The share of each bill a customer holds back until the job is accepted. It’s a contract asset until you bill its release, then it moves to accounts receivable.' },
  JobLosses: { label: 'Jobs: expected losses', type: 'Expense', help: 'When a long job is expected to cost more than its price, the whole loss is booked here at month-end close, not as the work goes on. It sits with cost of services.' },
  PaymentFees: { label: 'Card fees', type: 'Expense', help: 'What the card processor charges on each card payment.' },
  Inventory: { label: 'Inventory', type: 'Asset', help: 'The cost of the stock you hold.' },
  COGS: { label: 'Cost of goods sold', type: 'Expense', help: 'What the products you sold cost you.' },
  CostOfServices: { label: 'Cost of services', type: 'Expense', help: 'The direct cost of services you sell, such as subcontractors. Service lines on a vendor bill start on it.' },
  AccruedPurchases: { label: 'Goods received, not yet billed', type: 'Liability', help: 'Stock you’ve received before the vendor’s bill arrives (GRNI). It clears when the bill is posted.' },
  InventoryLoss: { label: 'Stock losses', type: 'Expense', help: 'Shrinkage, damage and write-downs found in counts. It sits under cost of goods sold.' },
  InventoryGain: { label: 'Stock gains', type: 'Expense', help: 'Extra stock found in counts. It sits under cost of goods sold and reduces it.' },
  PPE: { label: 'Property and equipment', type: 'Asset', help: 'Equipment, furniture, vehicles and other things you use for more than a year. A new asset starts on it.' },
  AccumDep: { label: 'Accumulated depreciation', type: 'Asset', help: 'The depreciation charged on fixed assets so far. It reduces their value on the balance sheet.' },
  DepreciationExpense: { label: 'Depreciation expense', type: 'Expense', help: 'Each month’s share of what fixed assets cost.' },
  PrepaidAsset: { label: 'Prepaid expenses', type: 'Asset', help: 'Costs paid in advance, such as a year’s insurance.' },
  PrepaidExpense: { label: 'Prepaid expenses used', type: 'Expense', help: 'Where a prepaid cost goes as each month is used up.' },
  RetainedEarnings: { label: 'Retained earnings', type: 'Equity', help: 'Where profit goes when a month is closed.' },
  OpeningBalanceEquity: { label: 'Opening balance equity', type: 'Equity', help: 'Takes the other side of each opening balance entered when you moved your books to ASYS. It should come to zero once every balance is in; move what’s left to owner’s equity or retained earnings with a journal entry.' },
  TillCash: { label: 'POS: cash taken', type: 'Asset', inherits: 'Cash', help: 'Cash taken at the POS. A store can have its own drawer under POS at each store.' },
  TillCard: { label: 'POS: card payments', type: 'Asset', inherits: 'Card', help: 'Where card payments at the POS land, less the card fee.' },
  TillSales: { label: 'POS: products sold', type: 'Revenue', inherits: 'Sales', help: 'Income from products sold at the POS. An item can have its own income account.' },
  TillServiceRevenue: { label: 'POS: services sold', type: 'Revenue', inherits: 'ServiceRevenue', help: 'Income from services sold at the POS.' },
  TillDiscounts: { label: 'POS: discounts given', type: 'Revenue', inherits: 'SalesDiscounts', help: 'Discounts given at the POS.' },
  InvoiceSales: { label: 'Invoices: products sold', type: 'Revenue', inherits: 'Sales', help: 'Income from products on invoices and credit memos. An item can have its own income account, and each invoice line can use another.' },
  InvoiceServiceRevenue: { label: 'Invoices: services sold', type: 'Revenue', inherits: 'ServiceRevenue', help: 'Income from services on invoices and credit memos.' },
  InvoiceDiscounts: { label: 'Invoices: discounts given', type: 'Revenue', inherits: 'SalesDiscounts', help: 'Discounts given on invoices.' },
  PaymentInCash: { label: 'Customer payments: cash', type: 'Asset', inherits: 'Cash', help: 'Where a customer’s cash payment goes, unless you pick another account on the payment.' },
  PaymentInBank: { label: 'Customer payments: card, transfer and cheque', type: 'Asset', inherits: 'Card', help: 'Where a customer’s card, transfer or cheque payment goes, unless you pick another account on the payment.' },
  PaymentOutCash: { label: 'Vendor payments: cash', type: 'Asset', inherits: 'Cash', help: 'What a cash payment to a vendor comes from, unless you pick another account on the payment.' },
  PaymentOutBank: { label: 'Vendor payments: transfers and cheques', type: 'Asset', inherits: 'Card', help: 'What a transfer or cheque to a vendor comes from, unless you pick another account on the payment.' },
  ExpenseAccount: { label: 'Expenses: usually posted to', type: 'Expense', help: 'The expense account a new expense starts on, General expenses unless you choose another. You can change it on each expense.' },
  ExpensePaidFrom: { label: 'Expenses: usually paid from', type: null, inherits: 'Cash', help: 'The account a new expense starts as paid from. You can change it on each expense.' },
}

/** The transactions on Transaction accounts: each lists the accounts it posts to. */
export const TRANSACTIONS = [
  { id: 'till', title: 'POS sales', intro: 'A sale or refund at the POS.', rows: [
    ['TillCash', 'Cash taken'], ['TillCard', 'Card payments'], ['PaymentFees', 'Card fees'], ['AR', 'Sold on account'],
    ['TillSales', 'Products sold'], ['TillServiceRevenue', 'Services sold'], ['TillDiscounts', 'Discounts given'],
    ['SalesTaxPayable', 'Sales tax owed'], ['COGS', 'Cost of goods sold'], ['Inventory', 'Stock'],
  ] },
  { id: 'invoice', title: 'Customer invoices and credit memos', intro: 'An invoice, or a credit memo against one.', rows: [
    ['AR', 'Owed by customers'], ['InvoiceSales', 'Products sold'], ['InvoiceServiceRevenue', 'Services sold'],
    ['InvoiceDiscounts', 'Discounts given'], ['SalesTaxPayable', 'Sales tax owed'], ['COGS', 'Cost of goods sold'], ['Inventory', 'Stock'],
  ] },
  { id: 'payment_in', title: 'Customer payments', intro: 'A customer paying an invoice.', rows: [
    ['PaymentInCash', 'Cash payments go to'], ['PaymentInBank', 'Card, transfer and cheque go to'], ['AR', 'Owed by customers'],
  ] },
  { id: 'receive', title: 'Goods received and vendor bills', intro: 'Receiving stock, then posting the vendor’s bill.', rows: [
    ['Inventory', 'Stock'], ['AccruedPurchases', 'Goods received, not yet billed'], ['AP', 'Owed to vendors'], ['CostOfServices', 'Service lines start on'],
  ] },
  { id: 'payment_out', title: 'Vendor payments', intro: 'Paying a vendor’s bill.', rows: [
    ['PaymentOutCash', 'Cash comes from'], ['PaymentOutBank', 'Transfers and cheques come from'], ['AP', 'Owed to vendors'],
  ] },
  { id: 'expense', title: 'Expenses', intro: 'A running cost, paid now or on account.', rows: [
    ['ExpenseAccount', 'Usually posted to'], ['ExpensePaidFrom', 'Usually paid from'], ['AP', 'On account, owed to vendors'],
  ] },
  { id: 'stock', title: 'Stock counts and adjustments', intro: 'Counting stock and writing it up or down.', rows: [
    ['Inventory', 'Stock'], ['InventoryLoss', 'Stock losses'], ['InventoryGain', 'Stock gains'],
  ] },
  { id: 'assets', title: 'Fixed assets', intro: 'A new asset starts on these; each asset can use its own.', rows: [
    ['PPE', 'Asset cost'], ['AccumDep', 'Accumulated depreciation'], ['DepreciationExpense', 'Depreciation expense'],
  ] },
  { id: 'prepaids', title: 'Prepaid expenses', intro: 'A new prepaid cost starts on these.', rows: [
    ['PrepaidAsset', 'Paid in advance'], ['PrepaidExpense', 'Used up each month'],
  ] },
  { id: 'jobs', title: 'Jobs', intro: 'Deposits on jobs, and the month-end adjustment for long jobs (income as the work is done).', rows: [
    ['DeferredRevenue', 'Deposits are held in'], ['ContractAssets', 'Earned, not yet billed'],
    ['ContractLiabilities', 'Billed, not yet earned'], ['JobLosses', 'Expected losses'], ['Retainage', 'Retainage held by customers'],
  ] },
  { id: 'close', title: 'Month-end close and statements', intro: 'Closing a month, and where deposits sit on the balance sheet.', rows: [
    ['RetainedEarnings', 'Profit is moved to'], ['DeferredRevenue', 'Customer deposits are kept in'],
    ['OpeningBalanceEquity', 'Opening balances’ other side'],
  ] },
]

/** The transactions that use each account: { key: [titles] }. */
export const USED_BY = TRANSACTIONS.reduce((m, t) => {
  t.rows.forEach(([k]) => { (m[k] = m[k] || []).push(t.title) })
  return m
}, {})

// The 22 shared defaults every company needs (Account setup fills them in).
export const DEFAULT_ROLES = Object.entries(ACCOUNT_ROLES).filter(([, r]) => !r.inherits && r !== ACCOUNT_ROLES.ExpenseAccount).map(([key, r]) => ({ key, ...r }))
export const DEFAULT_LABEL = Object.fromEntries(Object.entries(ACCOUNT_ROLES).map(([k, r]) => [k, r.label]))
